import { logError, logNetRequest } from '@/utils/usefulFuncs';
import React, { createContext } from 'react';
import { AdvancedSettingsContext } from './AdvancedSettingsContext';
import { ModeContext } from './ModeContext';
import { handle400Statuses, sendApiRequest } from '@/services/api';
import { submitQueriesStream } from '@/services/genaiQueriesV2';
import { addAdvancedSettingValues } from '@/hooks/application/useAdvancedSettings';
import { runQualityCheck } from '@/services/application';

const RUNNING_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  REFINING: 'refining',
  ERROR: 'error',
};

// const forceResultState = {
//   resultId: 240498,
//   prevQueryFormData: {
//     question: 'Who is ceo',
//   },
// };
const forceResultState = {};

const getValidationErrorMessage = (validationResult) => {
  if (!validationResult) return null;
  if (typeof validationResult === 'string') return validationResult;
  if (typeof validationResult === 'object') {
    return validationResult.message || validationResult.error || null;
  }
  return null;
};

const defaultState = {
  resultId: null,
  prevQueryFormData: null,
  ...forceResultState,
  runningStatus: RUNNING_STATUS.INACTIVE,
  error: null,

  submitQuery: () => {
    logError('submitQuery not initialized ');
  },
  getMoreDetailForCurrentResult: () => {
    logError('getMoreDetailForCurrentResult not initialized');
  },
  getFollowUpQuestionForCurrentResult: () => {
    logError('getFollowUpQuestionForCurrentResult not initialized');
  },
  reset: () => {
    logError('reset not initialized');
  },
};

const convertFormDataToApiFormat = (formData) => {
  const copy = { ...formData };
  if (formData.selectedDocs) {
    copy.doc_list = copy.selectedDocs.map((doc) => {
      if (typeof doc === 'object') return doc.id;
      return doc;
    });
    delete copy.selectedDocs;
  }
  if (formData.doc_ids) {
    copy.doc_list = copy.doc_ids;
    delete copy.doc_ids;
  }
  if (Object.prototype.hasOwnProperty.call(formData, 'agentName')) {
    copy.agent_name = copy.agentName;
    delete copy.agentName;
  }
  if (formData.tags) {
    delete copy.tags;
  }
  return copy;
};

const splitV1FormForV2 = (apiFormData) => {
  const {
    question,
    doc_list: docList,
    agent_name: agentName,
    ticker,
    streaming,
    displayQuestion,
    ...rest
  } = apiFormData;
  return {
    questions: question,
    docIds: docList,
    agentName,
    ticker,
    settings: rest,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'init': {
      return { ...state, ...action.payload };
    }
    case 'START_QUERY': {
      return {
        ...state,
        runningStatus: RUNNING_STATUS.RUNNING,
        resultId: null,
        prevQueryFormData: action.payload,
        error: null,
      };
    }
    case 'QUERY_COMPLETED': {
      return { ...state, runningStatus: RUNNING_STATUS.COMPLETED, resultId: action.payload, error: null };
    }
    case 'QUERY_ERROR': {
      return { ...state, runningStatus: RUNNING_STATUS.ERROR, resultId: null, error: action.payload };
    }
    case 'RESET': {
      return {
        ...state,
        resultId: null,
        prevQueryFormData: null,
        runningStatus: RUNNING_STATUS.INACTIVE,
        error: null,
      };
    }
    default: {
      return state;
    }
  }
};

export const QueryControllerProvider = ({ children }) => {
  const advancedSettingsValues = React.useContext(AdvancedSettingsContext);
  const { promptSuffix, submitEndpoint, mapSubmitPayload, validateSubmit, onAuthError } = React.useContext(ModeContext);
  const [state, dispatch] = React.useReducer(reducer, defaultState);

  const submitQuery = React.useCallback(
    async (paramsFormData, options = {}, advancedSettingsOverrides = {}) => {
      const advancedSettingsValuesConcat = { ...advancedSettingsValues, ...advancedSettingsOverrides };
      let formData = { ...paramsFormData };
      if (promptSuffix && formData.question) {
        formData.displayQuestion = formData.question;
        formData.question = `${formData.question} ${promptSuffix}`;
      }
      addAdvancedSettingValues(advancedSettingsValuesConcat, formData);
      if (Object.prototype.hasOwnProperty.call(formData, 'agentName')) {
        formData.agent_name = formData.agentName;
      }
      const validationError = getValidationErrorMessage(validateSubmit && validateSubmit(formData, options));
      if (validationError) {
        dispatch({ type: 'QUERY_ERROR', payload: validationError });
        return null;
      }
      dispatch({ type: 'START_QUERY', payload: formData });
      const baseApiFormData = convertFormDataToApiFormat(formData);
      const apiFormData = mapSubmitPayload
        ? mapSubmitPayload(
            { ...baseApiFormData },
            { formData, options, advancedSettingsValues: advancedSettingsValuesConcat }
          )
        : baseApiFormData;
      console.log('submitQuery', formData);
      console.log('apiFormData', apiFormData);

      if (submitEndpoint) {
        const { status, data, error } = await sendApiRequest('post', submitEndpoint, apiFormData);
        logNetRequest(status, data, error);
        handle400Statuses(status);
        if (status === 200) {
          dispatch({ type: 'QUERY_COMPLETED', payload: data.id });
          if (options.enableQualityCheck) {
            runQualityCheck(data.id);
          }
          return data.id;
        }
        dispatch({ type: 'QUERY_ERROR', payload: error });
        return null;
      }

      const v2Args = splitV1FormForV2(apiFormData);
      const result = await submitQueriesStream({ ...v2Args });
      if (result.status === 200) {
        dispatch({ type: 'QUERY_COMPLETED', payload: result.runId });
        if (options.enableQualityCheck) {
          runQualityCheck(result.runId);
        }
        return result.runId;
      }
      dispatch({ type: 'QUERY_ERROR', payload: result.error });
      return null;
    },
    [dispatch, advancedSettingsValues, promptSuffix, submitEndpoint, mapSubmitPayload, validateSubmit]
  );

  const getFollowUpQuestionForCurrentResult = React.useCallback(
    (question, formDataOverrides = {}, options = {}, advancedSettingsOverrides = {}) => {
      const formData = { ...state.prevQueryFormData, ...formDataOverrides, question };
      const advancedSettingsValuesConcat = { ...advancedSettingsValues, ...advancedSettingsOverrides };
      addAdvancedSettingValues(advancedSettingsValuesConcat, formData);
      return submitQuery(formData, options, advancedSettingsOverrides);
    },
    [dispatch, submitQuery]
  );

  const getMoreDetailForCurrentResult = React.useCallback(
    (answer, formDataOverrides = {}, options = {}, advancedSettingsOverrides = {}) => {
      const newQuestion = `Improve this answer by including more details, previous question was: "${state.prevQueryFormData.question}"; previous answer was: "${answer}"`;
      const formData = { ...state.prevQueryFormData, ...formDataOverrides, question: newQuestion };
      const advancedSettingsValuesConcat = { ...advancedSettingsValues, ...advancedSettingsOverrides };
      addAdvancedSettingValues(advancedSettingsValuesConcat, formData);
      return submitQuery(formData, options, advancedSettingsOverrides);
    },
    [dispatch, state.prevQueryFormData, submitQuery]
  );

  const reset = React.useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const value = React.useMemo(
    () => ({
      ...state,
      submitQuery,
      getMoreDetailForCurrentResult,
      getFollowUpQuestionForCurrentResult,
      reset,
    }),
    [
      state.runningStatus,
      state.resultId,
      state.prevQueryFormData,
      state.error,
      submitQuery,
      getMoreDetailForCurrentResult,
      getFollowUpQuestionForCurrentResult,
      reset,
    ]
  );
  return <QueryControllerContext.Provider value={value}>{children}</QueryControllerContext.Provider>;
};

export const QueryControllerContext = createContext(defaultState);
